import React, { useState } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useLocationHandling } from '../pages/LocationHandling/useLocationHandling';
import { useAuth } from '../../shared/contexts/AuthContext';
import NotificationSign from '../components/NotificationSign';
import api from '../../shared/api/axios';
import { useTranslation } from 'react-i18next';
import HighLogo from '../HighLogo';
import logo from "../../assets/logo.png";
import { Bell } from 'lucide-react';

type Props = {
  searchQuery: string;
  onSearchChange: (q: string) => void;
};

const GOLD = '#D4AF37';
const TEXT_DEFAULT = '#ccc';
const BG = '#111';
const BORDER_DEFAULT = '#222';

const GALLERY_HARD_RELOAD_KEY = 'gallery:hardReload';    
const FOLLOWING_HARD_RELOAD_KEY = 'following:hardReload';

const SidebarMain: React.FC<Props> = ({ searchQuery, onSearchChange }) => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { location, requestMyLocation } = useLocationHandling();
  const {logout } = useAuth();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const [locLoading, setLocLoading] = useState(false);

  const handleConfirmLocation = async () => {
    if (locLoading) return;
    setLocLoading(true);

    const current = `${routerLocation.pathname}${routerLocation.search || ''}`;

    try {
      await requestMyLocation();
    } catch (e: any) {
      const msg = String(e?.message || e);
      if (msg.toLowerCase().includes('consent')) {
        localStorage.removeItem('privacy:locationConsent');
        navigate(`/require-privacy-changeCL?return=${encodeURIComponent(current)}`, {
          replace: true,
        });
        return;
      }
      console.warn('Geolocation not available or denied by the browser:', e);
    } finally {
      setLocLoading(false);
    }
  };

  // Hard Loading Gallery
  const goToGalleryHard = () => {
    sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, '1');
    navigate(`/gallery?refresh=${crypto.randomUUID()}`);
  };

  // Smart gallery nav
  const goToGallerySmart = () => {
    const onGallery = routerLocation.pathname.startsWith('/gallery');
    if (onGallery) {
      sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, '1');
      navigate(`/gallery?refresh=${crypto.randomUUID()}`);
    } else {
      sessionStorage.removeItem(GALLERY_HARD_RELOAD_KEY);
      navigate('/gallery');
    }
  };

  const goToFollowingSmart = () => navigate('/following');
  const goToFollowingHard = () => {
    sessionStorage.setItem(FOLLOWING_HARD_RELOAD_KEY, '1');
    navigate(`/following?refresh=${Date.now()}`);
  };

  // ---- active-route helper
  const isActive = (path: string | string[]) => {
    const paths = Array.isArray(path) ? path : [path];
    return paths.some((p) =>
      p === '/' ? routerLocation.pathname === '/' : routerLocation.pathname.startsWith(p)
    );
  };

  // 🔍 dynamic search placeholder based on current route
  const searchPlaceholder = React.useMemo(() => {
    if (isActive('/mylikes')) {
      return t('mainSidebar.search.likesPlaceholder');
    }
    if (isActive('/following')) {
      return t('mainSidebar.search.followingPlaceholder');
    }
    // default / gallery and anything else
    return t('mainSidebar.search.galleryPlaceholder');
  }, [routerLocation.pathname, t]);

  // Location consent checker
  const getLocationConsent = async (): Promise<boolean> => {
    try {
      const { data } = await api.get('/privacy/preferences');
      const ok = !!data?.locationConsent;
      localStorage.setItem('privacy:locationConsent', String(ok));
      return ok;
    } catch {
      return localStorage.getItem('privacy:locationConsent') === 'true';
    }
  };

  const handleChangeLocationClick = async () => {
    const current = `${routerLocation.pathname}${routerLocation.search || ''}`;
    const consent = await getLocationConsent();
    const to = consent
      ? `/change-location-geo?from=${encodeURIComponent(current)}`
      : `/change-location-nogeo?from=${encodeURIComponent(current)}`;
    navigate(to);
  };

  // ---- full-width sidebar button
  const Button: React.FC<{
    to?: string;
    onClick?: () => void;
    children: React.ReactNode;
    activeMatch?: string | string[];
  }> = ({ to, onClick, children, activeMatch }) => {
    const [hovered, setHovered] = useState(false);
    const active = activeMatch ? isActive(activeMatch) : !!(to && isActive(to));
    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '8px 10px',
      marginBottom: '8px',
      backgroundColor: BG,                 // ✅ always BG
      color: TEXT_DEFAULT,                 // ✅ always default text color
      border: `1px solid ${active ? GOLD : hovered ? GOLD : BORDER_DEFAULT}`, // ✅ active = gold border
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      textAlign: 'left',
      fontSize: 14,
      transition: 'border-color 120ms ease, background-color 120ms ease, color 120ms ease',
    };
    

    return (
      <button
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => (to ? navigate(to) : onClick?.())}
      >
        {children}
      </button>
    );
  };

  const RED = '#D24444';

  const DangerButton: React.FC<{
    onClick?: () => void;
    children: React.ReactNode;
  }> = ({ onClick, children }) => {
    const [hovered, setHovered] = useState(false);
    const style: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '8px 10px',
      marginBottom: '8px',
      backgroundColor: BG,
      color: TEXT_DEFAULT,
      border: `1px solid ${hovered ? RED : BORDER_DEFAULT}`,
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      textAlign: 'left',
      fontSize: 14,
      transition: 'border-color 120ms ease',
    };
    return (
      <button
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={onClick}
      >
        {children}
      </button>
    );
  };

  // ---- compact inline button (for the left of the location text)
  const InlineButton: React.FC<{
    title?: string;
    onClick?: () => void;
    children: React.ReactNode;
  }> = ({ title, onClick, children }) => {
    const [hovered, setHovered] = useState(false);
    const style: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 8px',
      backgroundColor: BG,
      color: TEXT_DEFAULT,
      border: `1px solid ${hovered ? GOLD : BORDER_DEFAULT}`,
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 500,
      fontSize: 13,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    };
    return (
      <button
        title={title}
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => onClick?.()}
      >
        {children}
      </button>
    );
  };
  return (
    <>
      {/* Sidebar */}
      <aside style={sidebarStyle}>
        {/* Top bar inside Sidebar */}
        <div
          style={{
            position: "relative",          // ✅ allow absolute positioning inside
            display: "flex",
            alignItems: "center",
            padding: "12px 20px",
            marginBottom: "-20px",
            marginTop: "1px",
            color: "#fff",
            minHeight: 64,                 // ✅ gives space so absolute bell doesn't overlap weirdly
          }}
        >
          {/* Logo (left) */}
          <div
            style={{
              marginLeft: "-25%",
              marginTop: "9%",
              marginBottom: "13%",
            }}
          >
          <HighLogo
            src={logo}
            style={{ width: "70%" }}
            padding={0}
            onClick={goToGalleryHard}
          />
          </div>
  
          {/* Notification (pinned to right, independent of logo width) */}
  <div
  style={{
    position: "absolute",
    top: 34,
    right: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  }}
>
  <NotificationSign
    to="/notifications"
    icon={<Bell size={21} color="#ff8c00" strokeWidth={2.2} />}
  />
</div>
        </div>
  
        {/* Search */}
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            display: "block",
            backgroundColor: "#222",
            color: "#aaa",
            width: "90%",
            padding: "8px 10px",
            marginBottom: "14px",
            marginTop: "10px",
            borderRadius: "8px",
            border: "1px solid #231e1e",
            fontSize: "14px",
          }}
        />
  
        <hr
          style={{
            margin: "16px 0",
            borderColor: "#333",
            marginTop: "20px",
            marginBottom: "20px",
          }}
        />
  
        <nav>
          <Button onClick={goToGallerySmart} activeMatch="/gallery">
            🖼 {t("mainSidebar.buttons.gallery")}
          </Button>
  
          <Button
            onClick={handleChangeLocationClick}
            activeMatch={["/change-location-geo", "/change-location-nogeo"]}
          >
            🌍 {t("mainSidebar.buttons.changeLocation")}
          </Button>
  
          {/* Location row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "14px 0 6px",
            }}
          >
            <InlineButton
              title={t("mainSidebar.location.tooltip")}
              onClick={handleConfirmLocation}
            >
              📍
            </InlineButton>
  
            <div
              style={{
                color: "#bbb",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {location.city && location.country
  ? `${location.city}, ${location.country}`
  : t("mainSidebar.location.unknown")}
            </div>
          </div>
  
          <hr style={{ margin: "16px 0", borderColor: "#333" }} />
  
          <Button
            onClick={() =>
              isActive("/following") ? goToFollowingHard() : goToFollowingSmart()
            }
            activeMatch="/following"
          >
            ⭐ {t("mainSidebar.buttons.myPreferences")}
          </Button>
  
          <Button to="/mylikes" activeMatch="/mylikes">
            ❤️ {t("mainSidebar.buttons.myLikes")}
          </Button>
  
          <hr style={{ margin: "16px 0", borderColor: "#333" }} />
  
          <Button to="/chat" activeMatch="/chat">
            💬 {t("mainSidebar.buttons.chat")}
          </Button>
  
          <hr style={{ margin: "16px 0", borderColor: "#333" }} />
  
          <Button to="/profile" activeMatch="/profile">
            👻 {t("mainSidebar.buttons.myProfile")}
          </Button>
          <Button to="/settings/overview" activeMatch="/settings/overview">
            ⚙️ {t("mainSidebar.buttons.settings")}
          </Button>
  
        </nav>
  
        <hr style={{ margin: "16px 0", borderColor: "#333" }} />
  
        <Button
          onClick={() =>
            navigate("/gallery-posting", {
              state: { reset: true },
            })
          }
          activeMatch="/gallery-posting"
        >
          📷 {t("mainSidebar.buttons.upload")}
        </Button>
  
        <hr style={{ margin: "16px 0", borderColor: "#333" }} />
  
        <DangerButton onClick={handleLogout}>
          📕 {t("mainSidebar.buttons.logout")}
        </DangerButton>

      </aside>
    </>
  );
  
};

const sidebarStyle: React.CSSProperties = {
  width: '230px',
  background:
  'linear-gradient(145deg, rgba(5, 9, 20, 0.95), rgba(4, 7, 15, 0.95))',  color: '#bbb',
  padding: '20px',
  height: 'calc(100vh - 50px)',
  boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  position: 'fixed',
  marginTop: '-2%',
  overflow: 'hidden',
};

export default SidebarMain;
